import { useCallback } from 'react';

import { useSettingsStore } from '@/store';
import { AppConfigTypes } from '@/types/appConfigTypes';
import { hexToHslString } from '../helpers/color-converter';

// Validation utilities
const isValidHexColor = (color: unknown): color is string =>
  typeof color === 'string' &&
  /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(color.trim());

const isValidHslString = (hsl: unknown): hsl is string =>
  typeof hsl === 'string' && hsl.trim() !== '';

const safeHexToHsl = (hex: string): string | null => {
  try {
    const result = hexToHslString(hex);
    return isValidHslString(result) ? result : null;
  } catch {
    return null;
  }
};

// Color configuration mappings
const COLOR_PATHS = {
  darkBackground: ['ui', 'theming', 'colors', 'darkBackground'],
  lightBackground: ['ui', 'theming', 'colors', 'lightBackground'],
  primaryLight: ['ui', 'theming', 'colors', 'primary', 'light'],
  secondaryLight: ['ui', 'theming', 'colors', 'secondary', 'light'],
  primaryDark: ['ui', 'theming', 'colors', 'primary', 'dark'],
  secondaryDark: ['ui', 'theming', 'colors', 'secondary', 'dark'],
  primaryForegroundLight: [
    'ui',
    'theming',
    'colors',
    'primary',
    'foregroundLight',
  ],
  secondaryForegroundLight: [
    'ui',
    'theming',
    'colors',
    'secondary',
    'foregroundLight',
  ],
  primaryForegroundDark: [
    'ui',
    'theming',
    'colors',
    'primary',
    'foregroundDark',
  ],
  secondaryForegroundDark: [
    'ui',
    'theming',
    'colors',
    'secondary',
    'foregroundDark',
  ],
} as const;

const THEME_MAPPINGS = {
  light: {
    primaryLight: ['--primary', '--ring'],
    primaryForegroundLight: ['--primary-foreground'],

    secondaryLight: ['--secondary'],
    secondaryForegroundLight: ['--secondary-foreground'],

    lightBackground: ['--background', '--card', '--popover'],
    lightForeground: [
      '--foreground',
      '--card-foreground',
      '--popover-foreground',
    ],
  },
  dark: {
    primaryDark: ['--primary', '--ring'],
    primaryForegroundDark: ['--primary-foreground'],

    secondaryDark: ['--secondary'],
    secondaryForegroundDark: ['--secondary-foreground'],

    darkBackground: ['--background', '--card', '--popover'],
    darkForeground: [
      '--foreground',
      '--card-foreground',
      '--popover-foreground',
    ],
  },
} as const;

export const useThemeConfig = () => {
  const {
    lightThemeColors,
    darkThemeColors,
    themeMode,
    settings,
    setSettings,
    setDarkThemeColors,
    setLightThemeColors,
    setThemeMode,
    setHexColors,
  } = useSettingsStore();

  const setAppConfig = useCallback(
    (data: AppConfigTypes) => {
      if (!data?.ui?.theming) return;

      try {
        setSettings(data);

        // Set theme mode only if current preference is 'auto' (system)
        const mode = data.ui.theming.themeMode;
        if (
          themeMode === 'auto' &&
          mode &&
          (mode === 'auto' || ['light', 'dark'].includes(mode))
        ) {
          setThemeMode(mode);
        }

        // Extract and validate colors
        const colors = Object.fromEntries(
          Object.entries(COLOR_PATHS)
            .map(([key, path]) => [
              key,
              path.reduce((obj: any, prop) => obj?.[prop], data),
            ])
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            .filter(([_, value]) => isValidHexColor(value))
        );

        if (Object.keys(colors).length === 0) return;

        // Set hex colors
        setHexColors(colors);

        // Build and set theme colors
        (['light', 'dark'] as const).forEach((theme) => {
          const themeColors = Object.entries(THEME_MAPPINGS[theme]).reduce(
            (acc, [colorKey, cssVars]) => {
              const hexColor = colors[colorKey as keyof typeof colors];
              if (!hexColor) return acc;

              const hslColor = safeHexToHsl(hexColor);
              if (!hslColor) return acc;

              cssVars.forEach((cssVar) => {
                acc[cssVar] = hslColor;
              });
              return acc;
            },
            {} as Record<string, string>
          );

          if (Object.keys(themeColors).length > 0) {
            (theme === 'light' ? setLightThemeColors : setDarkThemeColors)(
              themeColors
            );
          }
        });
      } catch (error) {
        console.error('Error processing theme config:', error);
      }
    },
    [
      setDarkThemeColors,
      setHexColors,
      setLightThemeColors,
      setSettings,
      setThemeMode,
    ]
  );

  return {
    setAppConfig,
    lightThemeColors,
    darkThemeColors,
    themeMode,
    settings,
    setThemeMode,
  };
};
